/* eslint-disable no-param-reassign */
/* eslint-disable default-param-last */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import raf from 'raf';
import './style.scss';
import {
  currentDate,
  extendedDayJs,
  dayJsDuration,
  convertToDayJsObject,
} from 'utils/dateTimeUtils';

const scrollTo = (element, to, duration) => {
  // jump to target if duration zero
  if (duration <= 0) {
    raf(() => {
      element.scrollTop = to;
    });
    return;
  }
  const difference = to - element.scrollTop;
  const perTick = (difference / duration) * 10;

  raf(() => {
    element.scrollTop += perTick;
    if (element.scrollTop === to) return;
    scrollTo(element, to, duration - 10);
  });
};

function generateDuration(startTime, endTime) {
  const duration = dayJsDuration(endTime.diff(startTime));
  const hours = parseInt(duration.asHours(), 10);
  const minutes = parseInt(duration.asMinutes(), 10) % 60;
  return `(${hours === 0 ? '' : `${hours}h `}${
    minutes === 0 ? '' : `${minutes}m`
  })`;
}

const passingCondition = (allowNextDay, i, diff, steps) => {
  if (!allowNextDay) return i < diff;
  return i <= 24 * (60 / steps) - 1;
};

function generateTimeList(
  allowNextDay,
  startFromDayBegin,
  initalTime,
  steps,
  isNotRanged,
  baseTime,
  maxTime = currentDate().endOf('day'),
  checkAvailability,
) {
  const arr = [];
  const start = currentDate();
  const remainder = start.minute() % steps;
  const dateTime = extendedDayJs(start).subtract(remainder, 'minutes');

  let currentTime = isNotRanged ? dateTime : extendedDayJs(baseTime);

  if (startFromDayBegin) {
    currentTime = currentDate().startOf('day');
  }

  if (checkAvailability) {
    const startTime = currentDate();
    const remainderForAvailable = 15 - (startTime.minute() % 15);
    if (remainderForAvailable < 15) {
      currentTime = convertToDayJsObject(startTime).add(
        remainderForAvailable + 15,
        'minutes',
      );
    } else {
      currentTime = convertToDayJsObject(startTime).add(15, 'minutes');
    }
  }

  let i = 0;
  const timeDifference = dayJsDuration(
    maxTime.diff(extendedDayJs(currentTime)),
  );
  const diff = parseInt(timeDifference.asMinutes() / steps, 10);

  while (passingCondition(allowNextDay, i, diff, steps)) {
    if (startFromDayBegin && i === 0) {
      arr.push({
        id: i,
        value: currentTime.format('HH:mm'),
        duration: generateDuration(baseTime, currentTime),
        stringValue: JSON.stringify({
          id: i,
          value: currentTime.format('HH:mm'),
        }),
        time: currentTime.format('YYYY-MM-DD HH:mm:ss'),
      });
    }

    currentTime = currentTime.add(steps, 'minute');

    arr.push({
      id: i,
      value: currentTime.format('HH:mm'),
      duration: generateDuration(baseTime, currentTime),
      stringValue: JSON.stringify({
        id: i,
        value: currentTime.format('HH:mm'),
      }),
      time: currentTime.format('YYYY-MM-DD HH:mm:ss'),
    });
    i += 1;
  }
  return arr;
}

function isValidFormat(timeString) {
  if (!timeString.includes(':')) return false;
  const timeParts = timeString.split(':');
  if (timeParts[0] < 0 || timeParts[0] > 23) return false;
  if (timeParts[1] < 0 || timeParts[1] > 59) return false;
  return true;
}

function setValidFormat(timeString) {
  try {
    if (!timeString) return currentDate().add(5, 'minute').format('HH:mm');

    if (timeString.includes(':')) {
      return formatTimeWithColon(timeString);
    } else if (timeString.length > 2 || timeString.length === 2) {
      return formatTimeWithoutColon(timeString);
    }
    return setValidFormat(`0${timeString[0]}:00`);
  } catch (ex) {
    return '12:00';
  }
}

function formatTimeWithColon(timeString) {
  const resetValue = [];
  const timeParts = timeString.split(':');
  if (timeParts[0] < 0) resetValue.push('00');
  else if (timeParts[0] > 23) resetValue.push('23');
  else resetValue.push(timeParts[0]);
  if (timeParts[1] < 0) resetValue.push('00');
  else if (timeParts[1] > 59) resetValue.push('59');
  else resetValue.push(timeParts[1]);
  if (resetValue[0].length === 1) resetValue[0] = `0${resetValue[0]}`;
  if (resetValue[1].length === 1) resetValue[1] = `0${resetValue[1]}`;
  return resetValue.join(':');
}

function formatTimeWithoutColon(timeString) {
  if (timeString.length === 3) {
    timeString = `0${timeString}`;
  }
  return setValidFormat(
    `${timeString.substring(0, 2)}:${
      timeString.length > 2 ? timeString.substring(2) : '00'
    }`,
  );
}

function TimePicker({
  id = '',
  defaultValue = currentDate(),
  durationCalculate,
  className = '',
  inputName = 'time',
  allowNextDay = false,
  startFromDayBegin = false,
  disabled = false,
  minuteStep = 1,
  isNotRanged = true,
  hideDropDown = false,
  inputClassName = '',
  maxTime = currentDate().endOf('day'),
  checkAvailability = false,
  label = 'timepicker',
  onChange = () => {},
  timeHandler = () => {},
}) {
  const [dropDownList, setDropDownList] = useState(
    generateTimeList(
      allowNextDay,
      startFromDayBegin,
      defaultValue,
      minuteStep,
      isNotRanged,
      durationCalculate,
      maxTime,
      checkAvailability,
    ),
  );
  const [selectedValue, setSelectedValue] = useState(dropDownList[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const inputRef = useRef(null);
  const dropDownRef = useRef(null);
  const containerRef = useRef(null);

  const onInputValueChange = date => {
    const [hours, minutes] = inputRef.current.value.split(':');
    const timeValue = extendedDayJs(defaultValue)
      .set('hour', hours)
      .set('minute', minutes.substring(0, 2));

    if (checkAvailability) {
      if (!date) {
        onChange(timeValue);
      }
    } else {
      onChange(timeValue);
    }
  };

  const onSelectChange = e => {
    try {
      inputRef.current.value = e.target.innerText;
      setSelectedValue(
        JSON.parse(e.target.getAttribute('data-dropdown-value')),
      );
      const date = e.target.getAttribute('data-duration');
      if (timeHandler) {
        timeHandler(extendedDayJs(date));
      }
      onInputValueChange(date);
      inputRef.current.focus();
    } catch (ex) {
      /* eslint-disable-next-line no-console */
      console.log(ex);
    }
  };

  const scrollToSelected = (duration, dropDownElement) => {
    const select = dropDownElement;
    const list = dropDownElement;
    if (!list) return;
    if (!dropDownList.length) return;

    let index =
      dropDownList.filter(x =>
        x.value.includes(inputRef.current.value.split(':')[0] || '00'),
      )[0]?.id || selectedValue.id;
    if (index < 0) index = 0;
    const topOption = dropDownElement.children[index];
    const to = topOption.offsetTop;
    scrollTo(select, to, duration);
  };

  const onInputBlur = e => {
    const inputDisplayValue = isValidFormat(e.target.value)
      ? e.target.value
      : setValidFormat(e.target.value);
    // eslint-disable-next-line react/prop-types
    const oldValue = defaultValue.format('HH:mm');

    if (inputDisplayValue !== oldValue) {
      inputRef.current.value = inputDisplayValue;
      onInputValueChange();
    }
  };

  const toggleDropDown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      setTimeout(() => {
        scrollToSelected(120, dropDownRef.current);
      }, 0);
    }
  };

  const scrollList = () => {
    setIsDropdownOpen(true);
    scrollToSelected(120, dropDownRef.current);
  };

  const onBlur = () => {
    setTimeout(() => {
      if (containerRef.current?.classList.contains('keyboard_focus')) {
        // Keep dropdown open for keyboard navigation
      } else {
        setIsDropdownOpen(false);
      }
    }, 250);
  };

  const onKeyboardFocus = () => {
    containerRef.current?.classList.add('keyboard_focus');
  };

  useEffect(() => {
    inputRef.current.value = extendedDayJs(defaultValue).format('HH:mm');
  }, [defaultValue]);

  useEffect(() => {
    setDropDownList(
      generateTimeList(
        allowNextDay,
        startFromDayBegin,
        defaultValue,
        minuteStep,
        isNotRanged,
        durationCalculate || currentDate(),
        maxTime,
        checkAvailability,
      ),
    );
  }, [defaultValue, durationCalculate, startFromDayBegin]);

  useEffect(() => {
    const handleFocusOutside = event => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleFocusOutside);
    document.addEventListener('focusin', handleFocusOutside);

    return () => {
      document.removeEventListener('mousedown', handleFocusOutside);
      document.removeEventListener('focusin', handleFocusOutside);
    };
  }, []);

  const isNumFieldValidate = e => {
    const key = e.keyCode || e.which;
    if ((key < 48 || key > 57) && e.key !== ':') {
      if (e.preventDefault) e.preventDefault();
      e.returnValue = false;
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Tab') {
      onKeyboardFocus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleDropDown();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setIsDropdownOpen(false);
      inputRef.current.focus();
    }
  };

  const handleListItemKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelectChange(e);
      setIsDropdownOpen(false);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setIsDropdownOpen(false);
      inputRef.current.focus();
    } else if (
      e.key === 'Tab' &&
      dropDownList.length ===
        // eslint-disable-next-line radix
        parseInt(e.currentTarget.getAttribute('data-id')) + 1
    ) {
      inputRef.current.focus();
    }
  };

  return (
    <div
      className={`tv-timepicker__container ${className} ${
        isDropdownOpen ? 'active' : ''
      }`}
      onBlur={onBlur}
      ref={containerRef}
      aria-label={label}
    >
      <input
        type="text"
        id={id}
        name={inputName}
        className={`tv-timepicker__input ${inputClassName}`}
        ref={inputRef}
        onBlur={onInputBlur}
        onFocus={toggleDropDown}
        onInput={scrollList}
        onKeyPress={isNumFieldValidate}
        disabled={disabled}
        maxLength="5"
        minLength="5"
        aria-label={label}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      />
      {!disabled && (
        <div
          className="tv-timepicker__select-option"
          onClick={toggleDropDown}
        />
      )}
      {!hideDropDown && isDropdownOpen && (
        <ul ref={dropDownRef} className="tv-timepicker__select-panel">
          {dropDownList.map(x => (
            <li
              key={x.stringValue}
              onClick={onSelectChange}
              data-dropdown-value={x.stringValue}
              data-duration={x.time}
              data-id={x.id}
              tabIndex={0}
              onKeyDown={handleListItemKeyDown}
            >
              {`${x.value} ${'  '} ${!isNotRanged ? x.duration : ''}`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

TimePicker.propTypes = {
  defaultValue: PropTypes.shape({}),
  id: PropTypes.string,
  className: PropTypes.string,
  inputName: PropTypes.string,
  disabled: PropTypes.bool,
  isNotRanged: PropTypes.bool,
  onChange: PropTypes.func,
  durationCalculate: PropTypes.shape({}),
  allowNextDay: PropTypes.bool,
  startFromDayBegin: PropTypes.bool,
  rest: PropTypes.shape({}),
  minuteStep: PropTypes.number,
  inputClassName: PropTypes.string,
  hideDropDown: PropTypes.bool,
  maxTime: PropTypes.shape({}),
  timeHandler: PropTypes.func,
  checkAvailability: PropTypes.bool,
  label: PropTypes.string,
};

export default TimePicker;
